import React, { useMemo, useRef } from 'react';

import { useCurrentFileNavigation } from 'src/components/diff-set/hooks/file-navigation';
import { useCurrentFileViewed } from 'src/components/diff-set/hooks/file-viewed';
import KeyboardShortcuts from 'src/components/keyboard-shortcut-handler';
import { useIntl } from 'src/hooks/intl';
import approvalMessages from 'src/sections/repository/sections/pull-request/components/header-actions/approval-button.i18n';
import singleFileNavigationMessages from 'src/sections/repository/sections/pull-request/components/single-file-mode/single-file-navigation.i18n';
import requestChangesMessages from 'src/sections/repository/sections/pull-request/containers/request-changes-button.i18n';

import { TabSelectedType } from '../pull-request-tabbed';

import { KeyboardShortcutsContext } from './pull-request-keyboard-shortcuts-context';
import { keyboardShortcutMessages } from './pull-request-keyboard-shortcuts.i18n';

type PullRequestKeyboardShortcutsProps = {
  children: React.ReactNode;
  switchTab: (tab: TabSelectedType) => void;
};

type ShortcutRefHandler = () => void;

/* eslint @typescript-eslint/ban-types: "warn" */
export const PullRequestKeyboardShortcuts: React.FC<
  PullRequestKeyboardShortcutsProps
> = ({ children, switchTab }) => {
  const intl = useIntl();

  const approvePullRequestRef = useRef<ShortcutRefHandler>(() => {});
  const requestChangesRef = useRef<ShortcutRefHandler>(() => {});
  const finishReviewModalRef = useRef<ShortcutRefHandler>(() => {});
  const toggleFileTreeRef = useRef<ShortcutRefHandler>(() => {});
  const watchPullRequestRef = useRef<ShortcutRefHandler>(() => {});
  const fileViewedCallback = useCurrentFileViewed();
  const fileNavigationCallback = useCurrentFileNavigation();

  const shortcuts = useMemo(
    () => [
      {
        id: 'approve',
        description: intl.formatMessage(approvalMessages.approveAction),
        keys: 'A',
        handler: () => approvePullRequestRef.current?.(),
      },
      {
        id: 'request-changes',
        description: intl.formatMessage(
          requestChangesMessages.requestChangesAction
        ),
        keys: 'R',
        handler: () => requestChangesRef.current?.(),
      },
      {
        id: 'overview-tab',
        description: intl.formatMessage(
          keyboardShortcutMessages.overviewTabShortcut
        ),
        keys: '1',
        handler: () => switchTab('overview'),
      },
      {
        id: 'files-changed-tab',
        description: intl.formatMessage(
          keyboardShortcutMessages.filesChangedTabShortcut
        ),
        keys: '2',
        handler: () => switchTab('diff'),
      },
      {
        id: 'commits-tab',
        description: intl.formatMessage(
          keyboardShortcutMessages.commitsTabShortcut
        ),
        keys: '3',
        handler: () => switchTab('commits'),
      },
      {
        id: 'file-tree-toggle',
        description: intl.formatMessage(
          keyboardShortcutMessages.fileTreeToggleCollapseShortcut
        ),
        keys: 'f',
        handler: () => toggleFileTreeRef.current?.(),
      },
      {
        id: 'scroll-next-file',
        description: intl.formatMessage(singleFileNavigationMessages.nextFile),
        keys: 'j',
        handler: () => fileNavigationCallback('next'),
      },
      {
        id: 'scroll-previous-file',
        description: intl.formatMessage(
          singleFileNavigationMessages.previousFile
        ),
        keys: 'k',
        handler: () => fileNavigationCallback('prev'),
      },
      {
        id: 'finish-review',
        description: intl.formatMessage(keyboardShortcutMessages.finishReview),
        keys: 's',
        handler: () => finishReviewModalRef.current?.(),
      },
      {
        id: 'watch-pull-request',
        description: intl.formatMessage(
          keyboardShortcutMessages.watchPullRequestShortcut
        ),
        keys: 'w',
        handler: () => watchPullRequestRef.current?.(),
      },
      {
        id: 'file-viewed',
        description: intl.formatMessage(
          keyboardShortcutMessages.markFileViewedShortcut
        ),
        keys: 'v',
        handler: () => fileViewedCallback(),
      },
    ],
    [intl, switchTab, fileNavigationCallback, fileViewedCallback]
  );

  const keyboardShortcutContextValue = useMemo(
    () => ({
      finishReviewModalRef,
      approvePullRequestRef,
      requestChangesRef,
      toggleFileTreeRef,
      watchPullRequestRef,
    }),
    []
  );

  return (
    <KeyboardShortcutsContext.Provider value={keyboardShortcutContextValue}>
      <KeyboardShortcuts
        groupId="pull-request"
        groupTitle={intl.formatMessage(
          keyboardShortcutMessages.pullRequestKeyboardShortcutsTitle
        )}
        shortcuts={shortcuts}
      >
        {children}
      </KeyboardShortcuts>
    </KeyboardShortcutsContext.Provider>
  );
};
